import { orderSourceLabel } from '@/utils/orderSource';
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Trash2, Edit, ChevronDown, ChevronUp, FileText, AlertTriangle, Printer, Flag, CheckCircle2, ScrollText, WifiOff } from 'lucide-react';
import { formatDeliveryDateDisplay } from '../utils/dateUtils';
import { formatPhoneNumberDisplay } from '../utils/phoneUtils';
import StatusBadge from '@/components/StatusBadge';
import ProofOfDelivery from '@/components/ProofOfDelivery';
import DuplicateWarningPopover from '@/components/DuplicateWarningPopover';
import usePreparedPrint from '@/hooks/usePreparedPrint';
import useOrderSheetPrint from '@/hooks/useOrderSheetPrint';
import { formatFieldLabel } from '@/utils/fieldLabels';
import AuthenticatedDocumentLink from '@/components/AuthenticatedDocumentLink';
import { API } from '@/config/constants';
import { isDesktopOffline } from '@/api/desktopSync';
import { formatOrderValue } from '@/utils/orderValue';
import OrderNetValue from '@/components/OrderNetValue';

const WARNING_LABELS = {
  missing_delivery_date: 'Missing delivery date',
  missing_internal_order_number: 'Internal order number not assigned',
  missing_product_description: 'Missing product description',
  line_items_fetch_failed: 'Stripe line items could not be fetched',
  ai_extraction_failed: 'AI extraction failed during Stripe intake',
  source_document_storage_failed: 'Source snapshot could not be stored',
};

const formatWarningLabel = (warning) => WARNING_LABELS[warning] || warning.replace(/_/g, ' ');


export const OrderRow =({ order: initialOrder, isExpanded, onToggleExpand, onEdit, onDelete, deleteArmed = false, sortBy, includeOrderNumberOnPrint = true, actionPending = false, onDuplicateDecision, netAmount, onRetryNet }) => {
  const [order, setOrder] = useState(initialOrder);
  const warnings = order.ingestion_warnings || [];
  const offline = isDesktopOffline();
  const localOrder = order.desktop_sync?.origin === 'local';
  // Editing a cloud order offline is still read-only. Deleting is not: this row
  // used to lock the delete button for every cloud order while offline, which
  // silently outranked the caller's own decision once offline deletion existed.
  // Whether deletion is offered is the caller's call -- it withholds `onDelete`
  // -- and the desktop backend refuses anything it should not accept.
  // See TCA-26081406.
  const offlineCloudOrder = offline && !localOrder;
  const documentLabel = order.network_source === 'Stripe' ? 'View Source Snapshot' : 'View Original';
  const formattedOrderValue = formatOrderValue(order.total_price);

  // Keep in sync with parent
  useEffect(() => { setOrder(initialOrder); }, [initialOrder]);

  const startTrifoldPrint = usePreparedPrint({
    kind: 'trifold', record: order, canPrint: order.id === initialOrder.id,
  });
  const handlePrintTrifold = (e) => {
    e.stopPropagation();
    startTrifoldPrint(undefined, { includeOrderNumber: includeOrderNumberOnPrint });
  };

  // Open the generated order sheet as a read-only viewer (with its own Print
  // button). This is the compact, info-dense way to read an order without
  // opening the edit screen.
  const openOrderSheet = useOrderSheetPrint({ order, canPrint: order.id === initialOrder.id,
    includeOrderNumber: includeOrderNumberOnPrint });
  const handleOpenOrderSheet = (e) => {
    e.stopPropagation();
    openOrderSheet();
  };

  return (
    <div
      className={`border rounded-xl overflow-hidden ml-1 ${
        localOrder ? 'border-orange-300 bg-orange-50' : ''
      }`}
      data-testid={`order-item-${order.id}`}
      data-desktop-origin={localOrder ? 'local' : 'cloud'}
    >
      {/* Collapsed Row - Single Line */}
      <div
        className={`flex items-center p-2 cursor-pointer transition-colors ${
          localOrder ? 'hover:bg-orange-100/70' : 'hover:bg-stone-50'
        }`}
        onClick={() => onToggleExpand(order.id)}
      >
        <button className="flex-shrink-0 text-stone-400 mr-2">
          {isExpanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden text-sm">
          {/* Internal Order Number Badge */}
          {order.internal_order_number && (
            <span className="px-1 py-0.5 bg-primary/10 text-primary text-xs font-mono rounded flex-shrink-0">
              {order.internal_order_number}
            </span>
          )}
          {localOrder && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-orange-200 px-2 py-0.5 text-[11px] font-medium text-orange-900 flex-shrink-0"
              title="Unsynced order — saved on this computer and waiting to sync."
              data-testid={`unsynced-order-badge-${order.id}`}
            >
              <WifiOff className="h-3 w-3" />
              {order.internal_order_number
                ? 'Unsynced'
                : `Offline #${order.desktop_sync?.temporary_sequence || '?'}`}
            </span>
          )}
          {/* Document Indicator */}
          {order.document_id && (
            <span 
              className="flex-shrink-0 text-green-600" 
              title="Original document stored"
              data-testid={`doc-indicator-${order.id}`}
            >
              <FileText className="h-4 w-4" />
            </span>
          )}
          {warnings.length > 0 && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 flex-shrink-0"
              data-testid={`ingestion-warning-badge-${order.id}`}
              title={warnings.map(formatWarningLabel).join(', ')}
            >
              <AlertTriangle className="h-3 w-3" />
              {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
            </span>
          )}
          {order.duplicate_warning && (
            <DuplicateWarningPopover order={order} onDecision={onDuplicateDecision} disabled={actionPending} />
          )}
          {/* Reviewed-for-accuracy (green) wins; else low-confidence AI flag (amber). */}
          {order.reviewed_by ? (
            <span
              className="inline-flex items-center text-emerald-600 flex-shrink-0"
              data-testid={`reviewed-badge-${order.id}`}
              title={`Reviewed by ${order.reviewed_by}${order.reviewed_at ? ' on ' + new Date(order.reviewed_at).toLocaleString() : ''}`}
            >
              <CheckCircle2 className="h-4 w-4" />
            </span>
          ) : order.low_confidence_fields?.length > 0 && (
            <span
              className="inline-flex items-center text-amber-500 flex-shrink-0"
              data-testid={`low-confidence-flag-${order.id}`}
              title={`AI wasn't fully sure — verify: ${order.low_confidence_fields.map(formatFieldLabel).join(', ')}`}
            >
              <Flag className="h-4 w-4" />
            </span>
          )}
          {/* Status Badge */}
          <StatusBadge 
            status={order.status || 'designed'} 
            size="sm" 
            className="flex-shrink-0"
          />
          <span className="font-medium text-stone-900 truncate" data-testid="order-recipient-name">
            {order.recipient_name || 'Unknown'}
          </span>
            {order.desktop_sync?.edit_status && <span className="text-xs text-amber-800" role="status">{order.desktop_sync.edit_status}</span>}
          {/* Desktop-only details */}
          {sortBy !== 'source' && (
            <>
              <span className="text-stone-500 hidden sm:inline">|</span>
              <span className="text-primary font-medium hidden sm:inline flex-shrink-0">
                {orderSourceLabel(order, 'Unknown Source')}
              </span>
            </>
          )}
          <span className="text-stone-500 hidden lg:inline">|</span>
          <span className="text-stone-600 hidden lg:inline flex-shrink-0">
            #{order.order_number || 'N/A'}
          </span>
        </div>
        {sortBy === 'date' && (
          <>
          <span className="hidden sm:block w-32 flex-shrink-0 ml-2 pr-3 text-right text-sm font-normal italic text-stone-500 tabular-nums"
            data-testid={`order-net-${order.id}`}>
            <OrderNetValue result={netAmount} onRetry={onRetryNet} />
          </span>
          <span
            className="min-w-[5rem] sm:min-w-0 sm:w-32 flex-shrink-0 ml-2 sm:ml-0 sm:border-l sm:border-stone-200 sm:pl-3 text-right text-sm font-semibold text-stone-700 tabular-nums break-words"
            data-testid={`order-value-${order.id}`}
            title={formattedOrderValue === '—' ? 'Order value unavailable' : 'Order value'}
          >
            {formattedOrderValue}
          </span>
          </>
        )}
        {/* Order sheet / Edit / Delete buttons - Desktop only in collapsed view */}
        <div className={`hidden sm:flex w-[8.5rem] justify-end items-center gap-1 flex-shrink-0 ml-2 ${
          sortBy === 'date' ? 'border-l border-stone-200 pl-2' : ''
        }`}>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleOpenOrderSheet}
            title="View order sheet"
            aria-label="View order sheet"
            data-testid={`order-sheet-${order.id}`}
          >
            <ScrollText className="h-4 w-4 text-stone-500" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title={offlineCloudOrder ? 'View order details offline' : 'Edit order'}
            disabled={actionPending}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(order);
            }}
            data-testid={`edit-order-${order.id}`}
          >
            <Edit className="h-4 w-4 text-primary" />
          </Button>
          {onDelete && (
            <Button
              variant={deleteArmed ? 'destructive' : 'ghost'}
              size="sm"
              disabled={actionPending}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(order.id);
              }}
              className={deleteArmed ? 'px-2' : undefined}
              title={deleteArmed ? 'Click again to confirm deletion' : 'Delete order'}
              aria-label={deleteArmed ? 'Confirm deletion' : 'Delete order'}
              data-testid={`delete-order-${order.id}`}
            >
              {deleteArmed ? (
                <>
                  <AlertTriangle className="h-4 w-4" />
                  <span className="ml-1 text-[10px] leading-none">Sure?</span>
                </>
              ) : (
                <Trash2 className="h-4 w-4 text-red-500" />
              )}
            </Button>
          )}
        </div>
      </div>
      
      {/* Expanded Details */}
      {isExpanded && (
        <div className={`px-2 pb-2 pt-1 border-t ${
          localOrder ? 'bg-orange-50' : 'bg-stone-50'
        }`}>
          {/* Mobile action buttons */}
          <div className="flex sm:hidden gap-2 mb-2 pl-6">
            <Button
              variant="outline"
              size="sm"
              title={offlineCloudOrder ? 'View order details offline' : 'Edit order'}
              disabled={actionPending}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(order);
              }}
              className="flex-1"
            >
              <Edit className="h-4 w-4 mr-1 text-primary" />
              Edit
            </Button>
            {onDelete && (
              <Button
                variant={deleteArmed ? 'destructive' : 'outline'}
                size="sm"
                disabled={actionPending}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(order.id);
                }}
                className="flex-1"
              >
                {deleteArmed ? (
                  <>
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    Tap again to delete
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-1 text-red-500" />
                    Delete
                  </>
                )}
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm pl-6">
            {/* Mobile-only info that's hidden in collapsed view */}
            {sortBy !== 'source' && (
              <div className="sm:hidden">
                <span className="font-medium text-stone-700">Source: </span>
                <span className="text-primary">{orderSourceLabel(order, 'Unknown Source')}</span>
              </div>
            )}
            <div className="lg:hidden">
              <span className="font-medium text-stone-700">Order #: </span>
              <span className="text-stone-900">{order.order_number || 'N/A'}</span>
            </div>
            {sortBy !== 'date' && (
              <div>
                <span className="font-medium text-stone-700">Delivery: </span>
                <span className="text-stone-900">
                  {order.delivery_date ? formatDeliveryDateDisplay(order.delivery_date) : 'N/A'}
                </span>
              </div>
            )}
            
            {/* Always shown in expanded view */}
            <div>
              <span className="font-medium text-stone-700">Street Address: </span>
              <span className="text-stone-900">
                {order.recipient_street_address
                  ? [order.recipient_street_address, order.recipient_city, order.recipient_state, order.recipient_zip_code].filter(Boolean).join(', ')
                  : 'N/A'}
              </span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Phone: </span>
                <span className="text-stone-900">{formatPhoneNumberDisplay(order.recipient_phone) || 'N/A'}</span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Apt, Suite, Unit, etc.: </span>
              <span className="text-stone-900">{order.recipient_street_address_2 || 'N/A'}</span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Accepted: </span>
              <span className="text-stone-900">
                {order.order_accepted_date ? formatDeliveryDateDisplay(order.order_accepted_date) : 'N/A'}
              </span>
            </div>
            <div>
              <span className="font-medium text-stone-700">Status: </span>
              <StatusBadge 
                status={order.status || 'designed'} 
                size="sm"
                showIcon={true}
              />
            </div>
            {warnings.length > 0 && (
              <div className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid={`ingestion-warning-list-${order.id}`}>
                <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                  <AlertTriangle className="h-4 w-4" />
                  Intake Warnings
                </div>
                <ul className="list-disc pl-5 space-y-1 text-amber-900">
                  {warnings.map((warning) => (
                    <li key={warning}>{formatWarningLabel(warning)}</li>
                  ))}
                </ul>
              </div>
            )}
            {order.card_message && (
              <div className="md:col-span-2">
                <span className="font-medium text-stone-700">Card Message: </span>
                <span className="text-stone-900 italic whitespace-pre-line">"{order.card_message}"</span>
              </div>
            )}
            {order.special_instructions && (
              <div className="md:col-span-2">
                <span className="font-medium text-stone-700">Special Instructions: </span>
                <span className="text-stone-900">{order.special_instructions}</span>
              </div>
            )}
            {/* Protected originals and generated trifold cards remain readable offline. */}
            <div className="md:col-span-2 flex flex-wrap items-center gap-x-4 gap-y-1">
              {order.document_id && (
                <span>
                  <span className="font-medium text-stone-700">Original Document: </span>
                  <AuthenticatedDocumentLink
                    documentKey={JSON.stringify([order.id, order.document_id, order.resolved_source || order.network_source, order.desktop_sync?.origin])}
                    url={`${API}/orders/${order.id}/document?stamp=${includeOrderNumberOnPrint ? 1 : 0}`}
                    orderNumber={order.internal_order_number || order.order_number}
                    className="text-primary hover:text-primary hover:underline inline-flex items-center gap-1"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`view-document-${order.id}`}
                  >
                    <FileText className="h-3 w-3" />
                    {documentLabel}
                  </AuthenticatedDocumentLink>
                </span>
              )}
              <button
                type="button"
                onClick={handlePrintTrifold}
                className="text-primary hover:text-primary hover:underline inline-flex items-center gap-1"
                data-testid={`print-trifold-${order.id}`}
              >
                <Printer className="h-3 w-3" />
                Print Trifold Card
              </button>
            </div>
            {/* Duplicate Warning Status */}
            {order.duplicate_warning && (
              <div className="md:col-span-2 mt-1 rounded-lg border border-orange-100 bg-orange-50/50 px-3 py-2" data-testid={`duplicate-status-${order.id}`}>
                <p className="text-xs font-semibold text-stone-700 mb-1.5">
                  Duplicate Status: {' '}
                  <span className={order.duplicate_warning.resolution === 'approved' ? 'text-green-700' : 'text-orange-700'}>
                    {order.duplicate_warning.resolution === 'approved' ? 'Approved' : 'Unaddressed'}
                  </span>
                  {' — '}
                  <span className="font-normal text-stone-500">
                    Similar to {order.duplicate_warning.matches.map(m => '#' + (m.internal_order_number || m.id.slice(0, 8))).join(', ')}
                  </span>
                </p>
                {order.duplicate_warning.changelog && order.duplicate_warning.changelog.length > 0 && (
                  <div className="space-y-0.5">
                    {order.duplicate_warning.changelog.map((entry, i) => (
                      <p key={entry.changed_at} className="text-[11px] text-stone-500">
                        <span className="font-medium text-stone-600">{entry.changed_by}</span>
                        {' marked as '}
                        <span className={entry.resolution === 'approved' ? 'text-green-700 font-medium' : 'text-orange-700 font-medium'}>
                          {entry.resolution}
                        </span>
                        {' on '}
                        {new Date(entry.changed_at).toLocaleString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                          hour: 'numeric', minute: '2-digit', hour12: true,
                        })}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Proof of Delivery - only renders for delivered orders with confirmation data */}
            <ProofOfDelivery order={order} copyEnabled={order.id === initialOrder.id} />
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderRow;
